import { useState } from 'react';
import { Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { resolveTranslation } from '@/utils/resolveTranslation';
import {
  translateSampleProfiles,
  translateShowAllDemographics,
  translateShowLess,
} from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import type { SampleProfile } from '@/types';

interface SampleProfileCardProps {
  sampleProfile?: SampleProfile | null;
  /**
   * TELV1-360 — project analysis language. Drives the heading and the
   * expand/collapse toggle copy. Default 'English' for any out-of-tree
   * caller; in-app callers pass through the project's `analysis_language`.
   */
  analysisLanguage?: string;
}

const COLLAPSED_FIELDS = 4;
const COLLAPSED_ITEMS = 5;

function SampleProfileCard({
  sampleProfile,
  analysisLanguage = 'English',
}: SampleProfileCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (!sampleProfile || sampleProfile.demographics.length === 0) return null;

  const sortedFields = sampleProfile.demographics
    .filter((f) => f.distribution.length > 0)
    .sort((a, b) => {
      const priority: Record<string, number> = { gender: 0, age: 1, social_class: 2, location: 3 };
      const aPri = priority[a.field] ?? 10;
      const bPri = priority[b.field] ?? 10;
      return aPri - bPri;
    });

  const fieldsToShow = expanded ? sortedFields : sortedFields.slice(0, COLLAPSED_FIELDS);
  const hasMoreFields = sortedFields.length > COLLAPSED_FIELDS;

  return (
    <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-4 w-4 text-neutral-400" />
        <h3 className="text-sm font-semibold text-neutral-900">
          {resolveTranslation(translateSampleProfiles, analysisLanguage)}
          <span className="font-normal text-neutral-500 ml-1">
            (n={sampleProfile.total_respondents})
          </span>
        </h3>
      </div>

      <div className="flex flex-col gap-4">
        {fieldsToShow.map((field) => {
          const total = field.distribution.reduce((sum, item) => sum + item.count, 0);
          const itemsToShow = expanded
            ? field.distribution
            : field.distribution.slice(0, COLLAPSED_ITEMS);

          return (
            <div key={field.field} className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-neutral-700 capitalize">
                {field.field.replace(/_/g, ' ')}
                {total > 0 && (
                  <span className="font-normal text-neutral-400 ml-1">(n={total})</span>
                )}
              </span>
              <div className="flex flex-col gap-1">
                {itemsToShow.map((item) => {
                  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={item.value} className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-1.5">
                        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
                          <div
                            className="h-full rounded-full bg-emerald-400"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span
                        className="text-xs text-neutral-600 max-w-[140px] text-right break-words"
                        title={item.value}
                      >
                        {item.value}
                      </span>
                      <span className="text-xs text-neutral-400 tabular-nums w-6 text-right">
                        {item.count}
                      </span>
                      <span className="text-xs text-neutral-400 tabular-nums w-8 text-right">
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {hasMoreFields && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          {expanded
            ? resolveTranslation(translateShowLess, analysisLanguage)
            : resolveTranslation(translateShowAllDemographics, analysisLanguage)}
        </button>
      )}
    </Card>
  );
}

export default SampleProfileCard;
